import { ChangeDetectorRef, Directive, Input, OnInit, ViewChild, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { IRequestCallback } from '@df/api/request/request.service';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { AbstractFinder } from '@df/catalog/abstract-finder/abstract-finder';
import { AbstractFinderService } from '@df/catalog/abstract-finder/abstract-finder.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import isEqual from 'lodash-es/isEqual';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';

@Directive()
export abstract class AbstractFinderComponent<T extends AbstractFinder, D extends Catalog.IFinderData> implements OnInit {
    protected abstract finderService: AbstractFinderService<T, D>;

    /**
     * Client service
     */
    readonly client = inject(Client);

    /**
     * Country code to use
     * @type {string | undefined}
     */
    @Input()
    country?: string;

    @Input()
    noStorage?: boolean;

    /**
     * Address to use with search
     * @type {string}
     */
    @Input('address')
    defaultAddress?: string;

    /**
     * Max number of findableModels to find when searching
     * (compare with page size)
     *
     * it defaults to 30 in prices server
     * @type {number}
     */
    @Input()
    limit?: number;

    /**
     * Max distance
     * @type {number}
     */
    @Input()
    maxDistance?: number;

    /**
     * Page size to use with pager
     * it defaults to 8
     * @type {number}
     */
    @Input()
    pageSize?: number;

    /**
     * if true we won't check geolocation on init
     * @type {boolean}
     */
    @Input()
    noGeolocationOnInit?: boolean;

    /**
     * Address to use with search
     * @type {string}
     */
    address?: string;

    /**
     * Max number of findableModels visible
     * (compare with limit and page size)
     *
     * @type {number}
     */
    showLimit: number = 0;

    /**
     * Models verbosity
     */
    protected loacateVerbosity = 1;
    /**
     * If true then we will not show all findableModels by default
     * @type {boolean}
     */
    defaultNone = false;

    /**
     * Status model for requests
     * @type {Status}
     */
    status = new Status();

    /**
     * Last search hash not to re run same thing while busy
     * @type {string}
     */
    protected lastSearchHash?: string;

    /**
     * Initial status model for requests
     * @type {Status}
     */
    initialStatus = new Status();

    /**
     * Array with items related to current search
     */
    protected _findableModels?: T[];

    /**
     * Flag is set to true when search results are for your particual
     * search and false when falling back to all items/default search
     * @type {boolean}
     */
    protected _isUserSearch: boolean = false;

    /**
     * Form
     * important: you need to put #form="ngForm" on form element in template
     */
    @ViewChild('form', { static: false })
    public form?: NgForm;

    /**
     * Currently visible step
     */
    protected set locationState(locationState: string | undefined) {
        this.hashStorageService.set('location', locationState);
    }
    protected get locationState(): string | undefined {
        return this.hashStorageService.get('location');
    }

    /**
     * Currently visible step
     */
    protected set geolocationState(value: Catalog.IFindableLocateCoordsOptions) {
        this.hashStorageService.set('geolocation', value);
    }
    protected get geolocationState(): Catalog.IFindableLocateCoordsOptions {
        return this.hashStorageService.get('geolocation');
    }

    /**
     * geo coordinates used for geolocation
     * @type {IFindableLocateCoordsOptions | undefined}
     */
    geolocation?: Catalog.IFindableLocateCoordsOptions;

    searchDebounced: () => void;

    protected hashStorageService = inject(HashStorageService);

    private timeoutService = inject(TimeoutService);

    constructor(protected _ref: ChangeDetectorRef) {
        this.searchDebounced = this.timeoutService.debounce(() => this.search(), 500);
    }

    /**
     * This magical construction was created cause geolocation may not return any calback,
     * on the other hand we dont want to send too many calls or show alls tores first then update to local ones.
     *
     * So search is debounced.
     * If we can get coordinates within 500ms we will only load by location.
     * If we wont have coords from location we will only load default stores.
     * If we will get coordinates later we will update search by location, but only if no manual search yet.
     */
    ngOnInit() {
        if (!this.noStorage) {
            this.address = this.locationState;
            this.geolocation = this.geolocationState;
        }
        if (this.address) {
            this.search(this.initialStatus);
        } else if (!this.noGeolocationOnInit || this.geolocation) {
            this.searchByGeolocation(this.initialStatus, true);
        }
    }

    /**
     * Performs search call and update stores with results
     * @param {Status}                                                  status
     * @param {IFindableLocateOptions | IFindableLocateCoordsOptions}   findData   Find request data
     * @param {IRequestCallback}                                        onError
     */
    search(
        status = this.status,
        findData?: Catalog.IFindableLocateOptions | Catalog.IFindableLocateCoordsOptions,
        onError?: IRequestCallback
    ): void {
        if (this.form) {
            this.form.submitAttempt = true;
            this.form.ngSubmit.emit();
        }

        const unsubmit = () => {
            if (this.form) {
                this.form.submitAttempt = false;
            }
        };

        if (!this.form || this.form.valid) {
            if (!findData) {
                findData = {
                    address: <string>this.address,
                    country: this.country,
                    maxDistance: this.maxDistance,
                    verbosity: this.loacateVerbosity
                };
            }

            this.locationState = this.address;

            if (this.limit && findData.limit === undefined) {
                findData.limit = this.limit;
            }

            const hash = JSON.stringify(findData);

            if (this.lastSearchHash !== hash) {
                this.lastSearchHash = hash;

                if (!onError) {
                    onError = () => this.loadAndShowAll();
                }

                if (
                    this.finderService.locate(
                        status,
                        findData,
                        (response: T[]) => {
                            unsubmit();
                            this.findableModels = sortBy(
                                uniq(
                                    response.filter(
                                        findableModel => findableModel.data && findableModel.data.latitude && findableModel.data.longitude
                                    )
                                ),
                                'distance'
                            );
                            this._isUserSearch = true;

                            this.onFindableModelsUpdate();
                        },
                        onError
                    ) !== false
                ) {
                    return;
                }

                // if no stores picked then show all
                this.loadAndShowAll(this.status);
                unsubmit();
            }
        }
    }

    /**
     * Triggers search using the current location obtained from browser.
     * @param {Status}  status
     * @param {boolean} alwaysTrigger
     * @param {boolean} useStorage
     */
    searchByGeolocation(status = this.status, alwaysTrigger?: boolean, useStorage?: boolean): void {
        const fallbackSearch = () => this.reset(status);

        const coordsSearch = () => {
            this.address = undefined;

            this.hashStorageService.remove('location');
            this.search(status, this.geolocation, fallbackSearch);
        };

        const search = this.timeoutService.debounce(() => {
            this.geolocation ? coordsSearch() : fallbackSearch();
        }, 500);

        if (useStorage && this.geolocation) {
            search();
        } else {
            try {
                navigator.geolocation.getCurrentPosition(response => {
                    const coords = response.coords;

                    if (coords) {
                        this.geolocation = {
                            latitude: coords.latitude,
                            longitude: coords.longitude
                        };
                        this.geolocationState = this.geolocation;

                        search();
                    }
                });
            } catch (err) {}
            if (alwaysTrigger) {
                search();
            }
        }
    }

    reset(status = this.status): void {
        this.address = undefined;
        this.search(status, this.defaultAddress ? { address: this.defaultAddress, country: this.country } : undefined);
    }

    /**
     * Loads and shows all findableModels
     * if already loaded then will not send another request
     */
    loadAndShowAll(status?: Status): void {
        this.finderService.loadAll(status, 1, () => {
            this._isUserSearch = false;
            this.updateAllFindableModels();
        });
    }

    /**
     * Function triggered on findableModels search results.
     */
    onFindableModelsUpdate() {
        this.detectChanges();
    }

    /**
     * Function triggered if there is no findableModels search results.
     */
    updateAllFindableModels(): void {
        if (!this._isUserSearch) {
            this.findableModels = sortBy(this.allFindableModels, model => model.data!.sort_order);
            this.onFindableModelsUpdate();
        }
    }

    /**
     * Gets all models existing in resource
     * @returns {T[]}
     */
    get allFindableModels(): T[] {
        const findableModels = this.finderService.resource.getAllExisting();
        if (this.country) {
            return findableModels.filter(findableModel => findableModel.data && findableModel.data.country_id === this.country);
        }
        return findableModels;
    }

    /**
     * Sets all findableModels that are availble to view (unlimited)
     * @param {T[] | undefined} findableModels; if undefined all findableModels will be taken
     */
    set findableModels(findableModels: undefined | T[]) {
        if (!isEqual(this._findableModels, findableModels)) {
            this.showLimit = this.pageSize === undefined ? 8 : this.pageSize;
            this._findableModels = findableModels;
        }
    }

    /**
     * Gets current findableModels array
     * @return {T[] | undefined}
     */
    get findableModels(): T[] | undefined {
        return this._findableModels;
    }

    /**
     * @return {boolean}
     */
    get isUserSearch(): boolean {
        return this._isUserSearch;
    }

    /**
     * Increase showLimit
     */
    showMore(): void {
        this.showLimit += this.pageSize === undefined ? 8 : this.pageSize;
        this.detectChanges();
    }
    /**
     * Increase showLimit
     * @returns {boolean}
     */
    get hasMore(): boolean {
        return this._findableModels && this.showLimit < this._findableModels.length ? true : false;
    }

    trackByModelId(index: number, model: T): number {
        return model.id;
    }

    protected detectChanges() {
        this._ref.detectChanges();
    }
}
